import { buildDateTime } from "./commandeDraft";
import { findMatchingPatient, findMatchingProduit } from "./commandeDraftMerge";

// Modifications ciblées : produits seuls (PATCH /medicaments) ou commentaires de production seuls.

const PATIENT_FULL_SAVE_FIELDS = [
    "commandePatientId", "nom", "prenom", "dateNaissance", "classification",
    "commentaryProductionRobot", "commentaryProductionEtiquetage",
];
// Champs non patchables via /medicaments/{id} → save complet
const PRODUIT_FULL_SAVE_FIELDS = ["nom", "dosage", "posologie", "quantite", "siBesoin"];
const orNull = value => value ?? null;
const asIs = value => value;
// Champs patchables, dans l'ordre du PATCH envoyé.
const PATCHABLE_PRODUIT_FIELDS = [
    ["billingValidated", asIs],
    ["planificationValidated", asIs],
    ["ehpadWarnedIfOutOfStock", asIs],
    ["statut", orNull],
    ["classificationMedicament", orNull],
    ["commentaryPlanificationRobot", orNull],
    ["commentaryPlanificationEtiquetage", orNull],
    ["destination", asIs],
];

function differs(left, right, fields) {
    return fields.some(field => left[field] !== right[field]);
}

// Patch d'un produit, ou null si son changement impose un save complet.
function produitPatch(cm, om) {
    if (!cm.id || !om.id || cm.id !== om.id) return null;
    if (differs(cm, om, PRODUIT_FULL_SAVE_FIELDS)) return null;
    const p = {};
    PATCHABLE_PRODUIT_FIELDS.forEach(([field, toValue]) => {
        if (cm[field] !== om[field]) p[field] = toValue(cm[field]);
    });
    if (cm.deliveryDate !== om.deliveryDate) {
        p.deliveryDate = cm.deliveryDate || null;
        const bh = buildDateTime(cm.deliveryDate, cm.billingHourTime);
        if (bh) p.billingHour = bh;
    }
    if (cm.billingHourTime !== om.billingHourTime) {
        p.billingHour = buildDateTime(cm.deliveryDate, cm.billingHourTime) || null;
    }
    return p;
}

// Patches des produits d'un patient, ou null si un changement impose un save complet.
function patientProduitPatches(cp, origPats) {
    const op = findMatchingPatient(cp, origPats);
    if (!op) return null;
    // Changements au niveau patient → save complet
    if (differs(cp, op, PATIENT_FULL_SAVE_FIELDS)) return null;

    const cpProds = cp.produits || [];
    const opProds = op.produits || [];
    if (cpProds.length !== opProds.length) return null;

    const patches = [];
    for (const cm of cpProds) {
        const p = produitPatch(cm, findMatchingProduit(cm, opProds));
        if (p === null) return null;
        if (Object.keys(p).length > 0) patches.push({ id: cm.id, patch: p });
    }
    return patches;
}

// Calcule les patches par produit entre le draft courant et le draft original.
// Retourne null si un save complet est nécessaire (changement structurel),
// ou un tableau de { id, patch } pour les produits modifiés.
export function computeProduitPatches(current, original) {
    if (!current || !original) return null;

    // Changements au niveau commande → save complet
    if (!sameExactFields(current, original, PRODUCTION_COMMENT_COMMAND_FIELDS)) return null;

    const currPats = current.patients || [];
    const origPats = original.patients || [];
    if (currPats.length !== origPats.length) return null;

    const allPatches = [];
    for (const cp of currPats) {
        const patches = patientProduitPatches(cp, origPats);
        if (patches === null) return null;
        allPatches.push(...patches);
    }
    return allPatches;
}

export function samePersistedProduit(left, right) {
    const fields = [
        "id",
        "nom",
        "dosage",
        "posologie",
        "quantite",
        "destination",
        "statut",
        "siBesoin",
        "classificationMedicament",
        "deliveryDate",
        "billingHourTime",
        "billingValidated",
        "billingUsername",
        "planificationValidated",
        "planificationValidatedAt",
        "planificationValidatedBy",
        "commentaryPlanificationRobot",
        "commentaryPlanificationEtiquetage",
        "ehpadWarnedIfOutOfStock",
        "pharmaceuticalValidated",
        "managementValidated",
        "managementValidatedAt",
        "managementValidatedBy",
        "pharmaceuticalValidatedAt",
        "pharmaceuticalValidatedBy",
    ];
    return fields.every(field => Object.is(left?.[field] ?? null, right?.[field] ?? null));
}

export const PRODUCTION_COMMENT_COMMAND_FIELDS = [
    "ehpadId",
    "statut",
    "destination",
    "source",
    "serviceKey",
    "commentaire",
    "emailEmetteur",
];

export const PRODUCTION_COMMENT_PATIENT_FIELDS = [
    "commandePatientId",
    "id",
    "nom",
    "prenom",
    "dateNaissance",
    "classification",
    "pharmaceuticalValidated",
    "managementValidated",
];

export function sameExactFields(left, right, fields) {
    return fields.every(field => left?.[field] === right?.[field]);
}

export function sameProductionCommentProducts(currentProducts, originalProducts) {
    if (currentProducts.length !== originalProducts.length) return false;
    return currentProducts.every(currentProduct => {
        const originalProduct = findMatchingProduit(currentProduct, originalProducts);
        return Boolean(originalProduct && samePersistedProduit(currentProduct, originalProduct));
    });
}

// Patch des commentaires de production d'un patient : undefined s'ils n'ont pas changé, null si un autre changement
// impose un save complet.
function productionCommentPatch(cp, origPats) {
    const op = findMatchingPatient(cp, origPats);
    if (!op) return null;
    if (!sameExactFields(cp, op, PRODUCTION_COMMENT_PATIENT_FIELDS)) return null;
    if (!sameProductionCommentProducts(cp.produits || [], op.produits || [])) return null;

    const robotChanged = cp.commentaryProductionRobot !== op.commentaryProductionRobot;
    const etiquetageChanged = cp.commentaryProductionEtiquetage !== op.commentaryProductionEtiquetage;
    if (!robotChanged && !etiquetageChanged) return undefined;
    if (!cp.commandePatientId) return null;
    return {
        id: cp.commandePatientId,
        patch: {
            commentaryProductionRobot: cp.commentaryProductionRobot ?? null,
            commentaryProductionEtiquetage: cp.commentaryProductionEtiquetage ?? null,
        },
    };
}

export function computeProductionCommentPatches(current, original) {
    if (!current || !original) return null;

    if (!sameExactFields(current, original, PRODUCTION_COMMENT_COMMAND_FIELDS)) return null;

    const currPats = current.patients || [];
    const origPats = original.patients || [];
    if (currPats.length !== origPats.length) return null;

    const patches = [];
    for (const cp of currPats) {
        const patch = productionCommentPatch(cp, origPats);
        if (patch === null) return null;
        if (patch) patches.push(patch);
    }
    return patches;
}
